import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  Plus,
  Search,
  MoreHorizontal,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Trash2,
  Hotel,
  Bus,
  Plane,
  Users,
  Eye,
  CalendarDays,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { AllocationModal } from '@/components/travel/allocations/allocation-modal';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';
import { format } from 'date-fns';

type AllocationType = 'HOTEL_ROOM' | 'TOUR_SEAT' | 'TRANSFER_VEHICLE' | 'FLIGHT_SEAT';
type AllocationStatus = 'ACTIVE' | 'FULLY_CONSUMED' | 'EXPIRED' | 'CANCELLED' | 'SUSPENDED';

const typeIcons: Record<AllocationType, typeof Hotel> = {
  HOTEL_ROOM: Hotel,
  TOUR_SEAT: Users,
  TRANSFER_VEHICLE: Bus,
  FLIGHT_SEAT: Plane,
};

const typeColors: Record<AllocationType, string> = {
  HOTEL_ROOM: 'bg-blue-100 text-blue-700',
  TOUR_SEAT: 'bg-purple-100 text-purple-700',
  TRANSFER_VEHICLE: 'bg-green-100 text-green-700',
  FLIGHT_SEAT: 'bg-sky-100 text-sky-700',
};

const statusColors: Record<AllocationStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  FULLY_CONSUMED: 'bg-orange-100 text-orange-700',
  EXPIRED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-100 text-red-700',
  SUSPENDED: 'bg-yellow-100 text-yellow-700',
};

export function AllocationsPage() {
  const { t } = useTranslation('allocations');
  const { t: tCommon } = useTranslation('common');
  const canCreate = usePermission('ALLOCATIONS', 'CREATE');
  const canEdit = usePermission('ALLOCATIONS', 'EDIT');
  const canDelete = usePermission('ALLOCATIONS', 'DELETE');
  const [debouncedSearch, setDebouncedSearch] = useSessionState<string>(
    'travel:allocations:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useSessionState<AllocationType | undefined>(
    'travel:allocations:typeFilter',
    undefined
  );
  const [statusFilter, setStatusFilter] = useSessionState<AllocationStatus | undefined>(
    'travel:allocations:statusFilter',
    undefined
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAllocationId, setEditingAllocationId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [allocationToDelete, setAllocationToDelete] = useState<string | null>(null);
  const limit = 12;

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, error } = trpc.allocation.list.useQuery({
    page,
    limit,
    search: debouncedSearch || undefined,
    allocationType: typeFilter,
    status: statusFilter,
  });

  const { data: stats } = trpc.allocation.getStats.useQuery();

  const utils = trpc.useUtils();

  const deleteMutation = trpc.allocation.delete.useMutation({
    onSuccess: () => {
      utils.allocation.list.invalidate();
      utils.allocation.getStats.invalidate();
      showSuccessToast(t('messages.deleted'));
      setDeleteConfirmOpen(false);
      setAllocationToDelete(null);
    },
    onError: (error) => {
      showErrorToast(error.message || t('messages.deleteError'));
    },
  });

  const handleAddAllocation = () => {
    setEditingAllocationId(null);
    setIsModalOpen(true);
  };

  const handleEditAllocation = (id: string) => {
    setEditingAllocationId(id);
    setIsModalOpen(true);
  };

  const handleDeleteAllocation = (id: string) => {
    setAllocationToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (allocationToDelete) {
      deleteMutation.mutate(allocationToDelete);
    }
  };

  const allocations = data?.allocations ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 12, total: 0, totalPages: 0 };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">
            {t('subtitle')}
            {pagination.total > 0 && ` (${pagination.total} ${tCommon('total')})`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/travel/allocations/calendar">
              <CalendarDays className="mr-2 h-4 w-4" />
              {t('calendarView')}
            </Link>
          </Button>
          {canCreate && (
            <Button onClick={handleAddAllocation}>
              <Plus className="mr-2 h-4 w-4" />
              {t('addAllocation')}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <div className="border-border bg-card border p-4">
            <p className="text-muted-foreground text-sm">{t('stats.total')}</p>
            <p className="text-2xl font-bold">{stats.totalAllocations}</p>
          </div>
          <div className="border-border bg-card border p-4">
            <p className="text-muted-foreground text-sm">{t('stats.active')}</p>
            <p className="text-2xl font-bold text-green-600">{stats.activeAllocations}</p>
          </div>
          <div className="border-border bg-card border p-4">
            <p className="text-muted-foreground text-sm">{t('stats.expiringSoon')}</p>
            <p className="text-2xl font-bold text-orange-600">{stats.expiringSoon}</p>
          </div>
          <div className="border-border bg-card border p-4">
            <p className="text-muted-foreground text-sm">{t('stats.utilization')}</p>
            <p className="text-2xl font-bold">{stats.utilizationRate}%</p>
          </div>
        </div>
      )}

      {/* Allocation Modal */}
      <AllocationModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        editId={editingAllocationId}
        onSuccess={() => {
          utils.allocation.list.invalidate();
          utils.allocation.getStats.invalidate();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('dialogs.delete.title')}
        description={t('dialogs.delete.description')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <select
          value={typeFilter ?? ''}
          onChange={(e) => {
            setTypeFilter((e.target.value as AllocationType) || undefined);
            setPage(1);
          }}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('filters.allTypes')}</option>
          <option value="HOTEL_ROOM">{t('types.hotelRoom')}</option>
          <option value="TOUR_SEAT">{t('types.tourSeat')}</option>
          <option value="TRANSFER_VEHICLE">{t('types.transferVehicle')}</option>
          <option value="FLIGHT_SEAT">{t('types.flightSeat')}</option>
        </select>
        <select
          value={statusFilter ?? ''}
          onChange={(e) => {
            setStatusFilter((e.target.value as AllocationStatus) || undefined);
            setPage(1);
          }}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('filters.allStatuses')}</option>
          <option value="ACTIVE">{t('status.active')}</option>
          <option value="FULLY_CONSUMED">{t('status.fullyConsumed')}</option>
          <option value="EXPIRED">{t('status.expired')}</option>
          <option value="CANCELLED">{t('status.cancelled')}</option>
          <option value="SUSPENDED">{t('status.suspended')}</option>
        </select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('errorLoading')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && allocations.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Calendar className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('empty.title')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {debouncedSearch || typeFilter || statusFilter
              ? t('empty.filtered')
              : t('empty.noAllocations')}
          </p>
          {!debouncedSearch && !typeFilter && !statusFilter && canCreate && (
            <Button className="mt-4" onClick={handleAddAllocation}>
              <Plus className="mr-2 h-4 w-4" />
              {t('addAllocation')}
            </Button>
          )}
        </div>
      )}

      {/* Allocations Grid */}
      {!isLoading && !error && allocations.length > 0 && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {allocations.map((allocation) => {
              const Icon = typeIcons[allocation.allocationType as AllocationType];
              const utilizationPercent =
                allocation.totalUnits > 0
                  ? Math.round((allocation.consumedUnits / allocation.totalUnits) * 100)
                  : 0;

              return (
                <div
                  key={allocation.id}
                  className="border-border bg-card group border p-5 transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-8 w-8 items-center justify-center ${typeColors[allocation.allocationType as AllocationType]}`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="line-clamp-1 font-semibold">{allocation.name}</h3>
                        <p className="text-muted-foreground text-sm">
                          {allocation.partner.companyName}
                        </p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/travel/allocations/${allocation.id}`}>
                            <Eye className="mr-2 h-4 w-4" />
                            {t('actions.viewDetails')}
                          </Link>
                        </DropdownMenuItem>
                        {canEdit && (
                          <DropdownMenuItem onClick={() => handleEditAllocation(allocation.id)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            {tCommon('edit')}
                          </DropdownMenuItem>
                        )}
                        {canDelete && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleDeleteAllocation(allocation.id)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {tCommon('delete')}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium ${typeColors[allocation.allocationType as AllocationType]}`}
                    >
                      {t(
                        `types.${allocation.allocationType === 'HOTEL_ROOM' ? 'hotelRoom' : allocation.allocationType === 'TOUR_SEAT' ? 'tourSeat' : allocation.allocationType === 'TRANSFER_VEHICLE' ? 'transferVehicle' : 'flightSeat'}`
                      )}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium ${statusColors[allocation.status as AllocationStatus]}`}
                    >
                      {t(
                        `status.${allocation.status === 'FULLY_CONSUMED' ? 'fullyConsumed' : allocation.status.toLowerCase()}`
                      )}
                    </span>
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('fields.availability')}</span>
                      <span className="font-medium">
                        {allocation.availableUnits} / {allocation.totalUnits}
                      </span>
                    </div>
                    <div className="bg-muted h-2 w-full">
                      <div
                        className={`h-2 ${
                          utilizationPercent >= 90
                            ? 'bg-red-500'
                            : utilizationPercent >= 70
                              ? 'bg-orange-500'
                              : 'bg-green-500'
                        }`}
                        style={{ width: `${utilizationPercent}%` }}
                      />
                    </div>
                  </div>

                  <div className="text-muted-foreground mt-4 flex items-center justify-between border-t pt-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>
                        {format(new Date(allocation.validFrom), 'MMM d')} -{' '}
                        {format(new Date(allocation.validTo), 'MMM d, yyyy')}
                      </span>
                    </div>
                    <span className="text-foreground font-medium">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: allocation.currency,
                      }).format(allocation.unitCost)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {t('pagination.showing', {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(pagination.page * pagination.limit, pagination.total),
                  total: pagination.total,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {tCommon('previous')}
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === pagination.page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className="w-8"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  {tCommon('next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
